// My Day: pure helpers for "which tickets does this page need".
//
// My Day used to be built only from `useTickets` — the newest 500 tickets of the
// inbox by creation date. A ticket you still own but that was created before the
// 500th newest one (a months-old thread the client keeps replying to) was not in
// that page, so it silently never showed under "My queue" / "Needs attention" /
// "Waiting on client" and wasn't counted in "My open".
//
// The page now ALSO asks the server directly for the tickets that belong to me
// (and to the teammates I'm covering for) and for the unassigned ones worth
// grabbing — `useMyDayTickets` in queries.ts — and merges them with the page
// here. No React / Supabase in this file so it is unit-tested directly.

/** Statuses that count as "open" on My Day. Mirrors `OPEN` in routes/my-day.tsx. */
export const MY_DAY_OPEN_STATUSES = ['new', 'in_progress', 'escalated', 'waiting_on_client'] as const;

/** Open but not parked — what's actionable right now. Mirrors `ACTIVE`. */
export const MY_DAY_ACTIVE_STATUSES = ['new', 'in_progress', 'escalated'] as const;

/** "Resolved · 7d" window. */
export const RESOLVED_WINDOW_MS = 7 * 86_400_000;

/** How many unassigned tickets to fetch for "Up for grabs" (the page shows 10;
 *  a few spare so snoozed / archived rows the client drops don't leave it short). */
export const UP_FOR_GRABS_FETCH = 30;

interface RowLike {
  id: string;
  updated_at?: string | null;
}

const stamp = (r: RowLike): number => (r.updated_at ? new Date(r.updated_at).getTime() || 0 : 0);

/**
 * Union of the newest-500 page and the direct fetch, one row per ticket id.
 * When both hold the ticket the fresher `updated_at` wins; on a tie the page
 * copy wins (an optimistic patch is applied to both caches alike).
 */
export function mergeTicketRows<T extends RowLike>(
  page: readonly T[] | undefined,
  extra: readonly T[] | undefined,
): T[] {
  const byId = new Map<string, T>();
  for (const r of page ?? []) byId.set(r.id, r);
  for (const r of extra ?? []) {
    const cur = byId.get(r.id);
    if (!cur || stamp(r) > stamp(cur)) byId.set(r.id, r);
  }
  return [...byId.values()];
}

/** Slack ids of the teammates I'm named as the backup for (approved leave
 *  periods only reach this list — the caller passes the active ones). */
export function coveredSlackIds(
  periods: Iterable<{ team_member_slack_id: string; backup_slack_id: string | null }>,
  mySlackId: string | null | undefined,
): string[] {
  if (!mySlackId) return [];
  const out = new Set<string>();
  for (const p of periods) if (p.backup_slack_id === mySlackId) out.add(p.team_member_slack_id);
  return [...out];
}

/** Whose tickets My Day loads directly: me plus the people I cover. Sorted so
 *  the query key is stable. Empty when I have no Slack id of my own. */
export function myDayOwnerIds(mySlackId: string | null | undefined, covered: readonly string[]): string[] {
  if (!mySlackId) return [];
  return [...new Set([mySlackId, ...covered])].sort();
}
